// React
import React, { Component } from 'react'
import PropTypes from 'prop-types'

// Libraries
import Tippy from '@tippyjs/react'
import { followCursor } from 'tippy.js'
import 'tippy.js/animations/shift-toward.css'

// Components
import PopoverContent from './Content'
import BPopover from 'ui/blocks/Popover'
import { POPOVER_Z_INDEX } from './constants'

/*
 * Automatic version of the Popover component. State is defined
 * within the component. Supports hover & click modes.
 *
 * <Popover method="hover" content="I'm a popover!">
 *   <span>Hover me!</span>
 * </Popover>
 */

const AVAILABLE_PLUGINS = {
  followCursor
}

export default class AutomaticPopover extends Component {
  static displayName = 'AutomaticPopover'

  static propTypes = {
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.string]),
    content: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.string]),
    isMobile: PropTypes.bool,
    // Offset in pixels
    offset: PropTypes.number,
    direction: PropTypes.oneOf([
      'top',
      'right',
      'bottom',
      'left',
      'top-start',
      'top-end',
      'right-start',
      'right-end',
      'bottom-start',
      'bottom-end',
      'left-start',
      'left-end'
    ]),
    method: PropTypes.oneOf(['click', 'hover', 'hover-click']),
    onOpen: PropTypes.func,
    onHide: PropTypes.func,
    onHidden: PropTypes.func,
    fixed: PropTypes.bool,
    delay: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
    animation: PropTypes.string,
    useContentWrapper: PropTypes.bool,
    useArrow: PropTypes.bool,
    fallbackPlacements: PropTypes.array,
    zIndex: PropTypes.number,
    target: PropTypes.string,
    noContentOverflow: PropTypes.bool,
    interactive: PropTypes.bool,
    defaultPopoverStyling: PropTypes.bool,
    unmountOnClose: PropTypes.bool,
    sticky: PropTypes.bool,
    stickyConfig: PropTypes.object,
    innerRef: PropTypes.func,
    withInertia: PropTypes.bool,
    plugins: PropTypes.object,
    appendTo: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
  }

  static defaultProps = {
    method: 'click',
    direction: 'right',
    animation: 'shift-toward',
    offset: 10,
    useArrow: true,
    useContentWrapper: true,
    interactive: true,
    unmountOnClose: true,
    onOpen: () => {},
    onClose: () => {},
    onHide: () => {},
    onHidden: () => {},
    zIndex: POPOVER_Z_INDEX
  }

  state = {
    opened: false
  }

  get modifiers () {
    const modifiers = []

    const { useArrow, fallbackPlacements } = this.props

    if (useArrow) {
      modifiers.push({
        name: 'arrow',
        options: {
          element: this.arrow
        }
      })
    }

    if (fallbackPlacements) {
      modifiers.push({
        name: 'flip',
        options: {
          fallbackPlacements
        }
      })
    }

    // Prevent tippy to adjust its position to the viewport, during test it can flip around like crazy
    // which could result in flay tests.
    if (window.tippyNoFlip) {
      const existingFlipModifier = modifiers.find((modifier) => modifier.name === 'flip')

      if (existingFlipModifier) {
        existingFlipModifier.enabled = false
      } else {
        modifiers.push({
          name: 'flip',
          enabled: false
        })
      }
    }

    modifiers.push({
      name: 'preventOverflow',
      options: {
        boundary: 'window',
        padding: window.innerHeight * 0.02
      }
    })

    return modifiers
  }

  get plugins () {
    const { plugins } = this.props

    const componentPlugins = []
    const componentProps = {}

    for (const plugin in plugins) {
      if (!['followCursor'].includes(plugin)) return null

      componentPlugins.push(AVAILABLE_PLUGINS[plugin])
      componentProps[plugin] = plugins[plugin]
    }

    return {
      plugins: componentPlugins,
      ...componentProps
    }
  }

  onOpen = () => {
    const { onOpen } = this.props

    // Keep track of opened state so child components
    // can fetch data onMount, if we don't do this the child
    // component will always be mounted. Note that this also leads
    // to better performance as child components are only rendered
    // when needed.
    this.setState({ opened: true })

    onOpen && onOpen()
  }

  onHidden = () => {
    const { onHidden, unmountOnClose } = this.props

    unmountOnClose && this.setState({ opened: false })

    onHidden?.()
  }

  handleCreate = (tippy) => {
    this.tippy = tippy
  }

  render () {
    const {
      children,
      content,
      direction,
      method,
      offset,
      isMobile,
      fixed,
      delay,
      useContentWrapper,
      animation,
      useArrow,
      zIndex,
      target,
      noContentOverflow,
      interactive,
      defaultPopoverStyling,
      sticky,
      stickyConfig,
      innerRef,
      withInertia,
      appendTo,
      onHide
    } = this.props

    let trigger = method
    if (method === 'hover') {
      // Support both real mouseenter and synthetic mouseover used in some tests
      trigger = 'mouseenter mouseover focus'
    } else if (method === 'hover-click') {
      trigger = 'mouseenter focus click'
    }

    const contentProps = {
      handleClose: this.tippy && this.tippy.hide
    }

    const inertiaProps = {
      inertia: true,
      moveTransition: 'transform 250ms cubic-bezier(.860, 0, .070, 1)'
    }

    return (
      <Tippy
        ref={(ref) => innerRef?.(ref)}
        zIndex={zIndex}
        placement={direction}
        interactive={interactive}
        interactiveBorder={10}
        offset={[0, offset]}
        animation={animation}
        onCreate={this.handleCreate}
        onShow={this.onOpen}
        onHidden={this.onHidden}
        onHide={onHide}
        appendTo={appendTo || document.body}
        trigger={trigger}
        delay={delay}
        {...(withInertia ? inertiaProps : {})}
        {...this.plugins}
        popperOptions={{
          strategy: fixed ? 'fixed' : 'absolute',
          modifiers: this.modifiers
        }}
        content={(
          <PopoverContent
            opened={this.state.opened}
            useContentWrapper={useContentWrapper}
            content={content}
            contentProps={contentProps}
            useArrow={useArrow}
            target={target}
            isMobile={isMobile}
            noOverflow={noContentOverflow}
            defaultPopoverStyling={defaultPopoverStyling}
          />
        )}
      >
        <BPopover.Wrapper sticky={sticky} stickyConfig={stickyConfig}>
          {children}
        </BPopover.Wrapper>
      </Tippy>
    )
  }
}
